import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Background,
  type Node,
  type NodeChange,
  type Edge,
  ReactFlow,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  Controls,
  MiniMap,
  BackgroundVariant,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { nodeTypes } from "./nodes";
import { LeftSidebar } from "./common/panels/leftSidebar";
import { createNodeByType } from "./utils/nodeFactory";
import {
  ArrowLeft,
  Pencil,
  Plus,
  RocketIcon,
  Workflow,
  Upload,
  Download,
  Loader,
  PlayCircle,
  Save,
  Cpu,
} from "lucide-react";
import { DeleteButtonEdge } from "./edge/deleteEdge";
import { useNavigate, useParams } from "react-router-dom";
import { getNodeTypeEnum } from "./enums/WorkflowNodeTypeEnums";
import {
  validateWorkflow,
  formatValidationErrors,
} from "./utils/nodeValidation";
import apiClient from "../api/api";
import { API_ENDPOINTS } from "../api/api.config";
import { ExitConfirmationModal } from "./components/exitConfirmationModal";
import ExecutionsPanel from "./common/panels/ExecutionsPanel";
import WorkflowTokenAnalytics from "./common/panels/WorkflowTokenAnalytics";

const proOptions = { hideAttribution: true };

const edgeTypes = {
  deleteButtonEdge: DeleteButtonEdge,
};

// Polling interval for checking execution status (in milliseconds)
const POLLING_INTERVAL = 2000; // 2 seconds
const MAX_POLLING_ATTEMPTS = 150; // 5 minutes max (150 * 2s = 300s)
const BACKGROUND_POLLING_INTERVAL = 3000; // 3 seconds for background polling

const deepClone = <T,>(value: T): T => {
  if (value === undefined || value === null) {
    return value;
  }
  return JSON.parse(JSON.stringify(value));
};

// Map a backend node_status string to the UI execution status. ONLY an
// explicitly-running status spins — unknown / not-started / queued nodes stay
// pending (null) so the canvas highlights exactly the node that's executing
// right now, like n8n (instead of every downstream node spinning at once).
const RUNNING_STATUSES = new Set([
  "RUNNING",
  "IN_PROGRESS",
  "IN-PROGRESS",
  "INPROGRESS",
  "PROCESSING",
  "EXECUTING",
  "STARTED",
]);
const DONE_STATUSES = new Set(["DONE", "SUCCESS", "COMPLETED", "COMPLETE"]);
const ERROR_STATUSES = new Set(["FAILED", "ERROR", "FAILURE"]);

const mapNodeStatus = (
  raw?: string | null,
): "running" | "success" | "error" | null => {
  if (!raw) return null;
  const s = String(raw).toUpperCase();
  if (DONE_STATUSES.has(s)) return "success";
  if (ERROR_STATUSES.has(s)) return "error";
  if (RUNNING_STATUSES.has(s)) return "running";
  return null; // NOT_STARTED, PENDING, QUEUED, etc. → no spinner
};

// Match a backend node_status entry to a canvas node (the backend keys status by
// node name, which may differ slightly from the node id).
const statusMatchesNode = (ns: any, node: any): boolean => {
  const sn = ns?.node_name || "";
  if (!sn) return false;
  const dataName = node?.data?.name || "";
  const id = node?.id || "";
  return (
    sn === dataName ||
    sn === id ||
    (!!dataName && (dataName.startsWith(sn) || sn.startsWith(dataName)))
  );
};

/**
 * The backend only records a node_status entry when a node finishes (DONE/FAILED)
 * — it never emits a per-node "running". So we derive the *currently running*
 * node(s) on the client as the execution frontier: a node that hasn't finished
 * yet, whose upstream nodes are ALL done, while the workflow is in progress.
 * This makes the spinner advance node-by-node like n8n.
 *
 * Returns a Map<nodeId, "running" | "success" | "error" | null>.
 */
const computeExecStatusMap = (
  prevNodes: Node<any>[],
  edges: Edge[],
  nodeStatusArr: any[],
  overallStatus?: string,
): Map<string, "running" | "success" | "error" | null> => {
  const statusArr = Array.isArray(nodeStatusArr) ? nodeStatusArr : [];
  const doneSet = new Set<string>();
  const failedSet = new Set<string>();

  for (const node of prevNodes) {
    const ns = statusArr.find((s) => statusMatchesNode(s, node));
    const mapped = mapNodeStatus(ns?.status);
    if (mapped === "success") doneSet.add(node.id);
    else if (mapped === "error") failedSet.add(node.id);
  }

  const overall = String(overallStatus || "").toUpperCase();
  const overallRunning =
    overall === "IN_PROGRESS" ||
    overall === "IN-PROGRESS" ||
    overall === "PROCESSING" ||
    overall === "RUNNING" ||
    overall === ""; // assume running if the backend didn't say otherwise

  // Map each node to the list of its upstream node ids.
  const predsByTarget = new Map<string, string[]>();
  for (const e of edges) {
    if (!e.target || e.source === "START") continue;
    const list = predsByTarget.get(e.target) || [];
    list.push(e.source);
    predsByTarget.set(e.target, list);
  }

  const result = new Map<string, "running" | "success" | "error" | null>();
  for (const node of prevNodes) {
    if (failedSet.has(node.id)) {
      result.set(node.id, "error");
    } else if (doneSet.has(node.id)) {
      result.set(node.id, "success");
    } else if (overallRunning) {
      // Frontier: a not-yet-finished node whose upstream are all done is the
      // one executing right now. Trigger/start nodes have no upstream → running.
      const preds = predsByTarget.get(node.id) || [];
      const allPredsDone = preds.every((p) => doneSet.has(p));
      result.set(node.id, allPredsDone ? "running" : null);
    } else {
      result.set(node.id, null);
    }
  }
  return result;
};

export function WorkFlowExecutions() {
  const { id: workflowId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [nodes, setNodes] = useState<Node<any>[]>([]);
  const [workflowDbId, setWorkflowDbId] = useState<string | null>(null);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);
  const [, setExecutionResults] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<"nodes" | "executions">("nodes");
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [, setPollingAttempts] = useState(0);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<any | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [updatedNodeIds, setUpdatedNodeIds] = useState<Set<string>>(new Set());
  const [hasIncompleteExecutionData, setHasIncompleteExecutionData] =
    useState(false);
  const [showTokenAnalytics, setShowTokenAnalytics] = useState(false);

  // Refs for node action callbacks
  const handleExecuteNodeRef = useRef<((id: string) => Promise<void>) | null>(
    null,
  );
  // Convert backend node type to UI node type
  const getUINodeType = (backendType: string): string => {
    const typeMap: Record<string, string> = {
      "FILE-UPLOAD": "FILE-UPLOAD",
      OCR: "OCR",
      "FILE-PARSE": "FILE-PARSE",
      LLM: "LLM",
      "FILE-DATABASE": "FILE-DATABASE",
      "document-store": "document-store",
      schedule: "schedule",
      conditional: "conditional",
      "compare-data": "compare-data",
      "file-export": "file-export",
      email: "email",
      export: "export",
      TRANSFORM: "TRANSFORM",
      HTTP: "HTTP",
      PDF: "PDF",
      "GEMINI-UNDERSTANDING": "GEMINI-UNDERSTANDING",
    };
    return typeMap[backendType] || backendType;
  };

  // Process nodes to unwrap data.input structure from API
  const processImportedNodes = (rawNodes: any[]): Node<any>[] => {
    // Deduplicate nodes by ID - keep the first occurrence
    const seen = new Set<string>();
    const uniqueNodes = rawNodes.filter((node) => {
      if (seen.has(node.id)) return false;
      seen.add(node.id);
      return true;
    });

    return uniqueNodes.map((node) => {
      const baseData = node.data?.inputs
        ? { ...node.data.inputs }
        : { ...node.data };
      delete baseData.inputs;
      delete baseData.outputs;

      // For HTTP nodes, always use "HttpNode" as the name
      const nodeName =
        node.type === "HTTP"
          ? "HttpNode"
          : node.name || baseData.name || node.id;

      return {
        id: node.id,
        type: getUINodeType(node.type),
        position: node.position,
        data: {
          ...baseData,
          name: nodeName,
          outputs: node.data?.outputs,
          onExecuteNode: (id: string) => handleExecuteNodeRef.current?.(id),
        },
      };
    });
  };

  // Inject template ID into HTTP nodes
  const injectTemplateIdToHttpNodes = (
    nodes: Node<any>[],
    templateId: string,
  ): Node<any>[] => {
    return nodes.map((node) => {
      if (node.type === "HTTP") {
        return {
          ...node,
          data: {
            ...node.data,
            name: "HttpNode", // Always set name to HttpNode for HTTP nodes
            request_id: templateId,
          },
        };
      }
      return node;
    });
  };

  const processInitialEdges = (rawEdges: any[]): Edge[] => {
    return rawEdges
      .filter((edge) => edge.source !== "START" && edge.target !== "END")
      .map((edge, index) => ({
        id: edge.id || `edge-${edge.source}-${edge.target}-${index}`,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        animated: edge.animated !== undefined ? edge.animated : false,
        type: edge.type || "deleteButtonEdge",
        data: edge.data,
      }));
  };

  const [edges, setEdges] = useState<Edge[]>(processInitialEdges([]));
  const [, setSelectedNode] = useState<Node<any> | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [title, setTitle] = useState("Document Parsing Workflow");
  const [version, setVersion] = useState("Version 1.0");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingVersion, setIsEditingVersion] = useState(false);
  const [, setValidationErrors] = useState<string>("");
  const [showExitConfirmModal, setShowExitConfirmModal] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [executionsRefreshKey, setExecutionsRefreshKey] = useState(0);

  const showToast = (message: string, duration = 3000) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), duration);
  };

  const titleSpanRef: any = useRef(null);
  const versionSpanRef: any = useRef(null);
  const titleInputRef: any = useRef(null);
  const versionInputRef: any = useRef(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backgroundPollingIntervalRef = useRef<any>(null);

  // Load workflow if ID is present in URL
  useEffect(() => {
    if (workflowId) {
      loadWorkflowById(workflowId);
    }
  }, [workflowId]);

  // Load workflow from API
  const loadWorkflowById = async (id: string) => {
    setIsLoadingWorkflow(true);
    try {
      // First, load the template
      const response = await apiClient.get(
        `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${id}`,
      );

      if (response.data.success && response.data.data) {
        const workflowData = response.data.data;

        setTitle(workflowData.title || "Document Parsing Workflow");
        setVersion(workflowData.description || "Version 1.0");
        setIsActive(workflowData.is_active ?? true);

        const templateId = workflowData._id || workflowData.id;
        setWorkflowDbId(templateId);
        localStorage.setItem("template_id", templateId);

        // Try to load execution history for this template
        try {
          const executionResponse = await apiClient.get(
            API_ENDPOINTS.WORKFLOW.EXECUTION_BY_TEMPLATE_ID(id),
          );

          if (executionResponse.data.success && executionResponse.data.data) {
            const executionData = executionResponse.data.data;

            // Set execution ID for display
            setExecutionId(executionData._id || executionData.id);

            // Process nodes with execution outputs
            if (executionData.nodes && Array.isArray(executionData.nodes)) {
              const processedNodes = processImportedNodes(executionData.nodes);

              // Use positions from template (latest) instead of execution snapshot (stale)
              const templatePositions = new Map<
                string,
                { x: number; y: number }
              >(
                (workflowData.nodes || []).map((n: any) => [
                  n.id,
                  n.position as { x: number; y: number },
                ]),
              );
              const nodesWithLatestPositions = processedNodes.map((node) => {
                const pos = templatePositions.get(node.id);
                return pos ? { ...node, position: pos } : node;
              });

              // Inject template ID into HTTP nodes
              const nodesWithTemplateId = injectTemplateIdToHttpNodes(
                nodesWithLatestPositions,
                templateId,
              );
              setNodes(nodesWithTemplateId);

              // Check overall execution status - always keep polling
              const overallStatus = executionData.overall_proccessing_status;
              const nodesWithOutputs = executionData.nodes.filter(
                (n: any) =>
                  n.data?.outputs && Object.keys(n.data.outputs).length > 0,
              ).length;
              const totalNodes = executionData.nodes.length;

              // Always keep polling regardless of status (IN_PROGRESS, DONE, FAILED, etc.)
              console.log(
                `🔄 Workflow status is ${overallStatus} (${nodesWithOutputs}/${totalNodes} nodes have outputs), polling will continue`,
              );
              setHasIncompleteExecutionData(true);
            } else {
              // No execution nodes, use template nodes and start polling
              const processedNodes = processImportedNodes(workflowData.nodes);
              const nodesWithTemplateId = injectTemplateIdToHttpNodes(
                processedNodes,
                templateId,
              );
              // Start polling to check for execution updates
              setHasIncompleteExecutionData(true);
              setNodes(nodesWithTemplateId);
            }

            if (executionData.edges) {
              const processedEdges = processInitialEdges(executionData.edges);
              setEdges(
                processedEdges.map((edge) => ({
                  ...edge,
                  data: { ...edge.data, onEdgeDelete: onEdgesDelete },
                })),
              );
            }
          } else {
            // No execution history found, use template data and start polling
            const processedNodes = processImportedNodes(workflowData.nodes);
            const nodesWithTemplateId = injectTemplateIdToHttpNodes(
              processedNodes,
              templateId,
            );
            setNodes(nodesWithTemplateId);
            // Start polling to check for new executions
            setHasIncompleteExecutionData(true);

            const processedEdges = processInitialEdges(workflowData.edges);
            setEdges(
              processedEdges.map((edge) => ({
                ...edge,
                data: { ...edge.data, onEdgeDelete: onEdgesDelete },
              })),
            );
          }
        } catch (executionError: any) {
          // If execution history load fails, just use template data and start polling
          console.log(
            "ℹ️ No execution history found, using template data and starting polling",
          );
          const processedNodes = processImportedNodes(workflowData.nodes);
          const nodesWithTemplateId = injectTemplateIdToHttpNodes(
            processedNodes,
            templateId,
          );
          setNodes(nodesWithTemplateId);
          // Start polling to check for new executions
          setHasIncompleteExecutionData(true);

          const processedEdges = processInitialEdges(workflowData.edges);
          setEdges(
            processedEdges.map((edge) => ({
              ...edge,
              data: { ...edge.data, onEdgeDelete: onEdgesDelete },
            })),
          );
        }

        setIsDirty(false);
        console.log("✅ Workflow loaded successfully:", workflowData);
        showToast("✅ Workflow loaded successfully!");
      }
    } catch (error: any) {
      console.error("❌ Failed to load workflow:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load workflow.";
      showToast(`Error: ${errorMessage}`);
      navigate("/workflow");
    } finally {
      setIsLoadingWorkflow(false);
    }
  };

  useEffect(() => {
    if (!isDirty && !isLoadingWorkflow) setIsDirty(true);
  }, [nodes, edges, title, version, isActive]);

  useEffect(() => {
    if (isEditingVersion && versionInputRef.current && versionSpanRef.current) {
      versionInputRef.current.style.width =
        versionSpanRef.current.offsetWidth + 12 + "px";
    }
  }, [version, isEditingVersion]);

  const handleTitleBlur = () => setIsEditingTitle(false);
  const handleVersionBlur = () => setIsEditingVersion(false);

  const onNodesChange = useCallback(
    (changes: NodeChange<any>[]) =>
      setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onEdgesChange = useCallback(
    (changes: any[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  const onEdgesDelete = useCallback((edgeId: string) => {
    setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
  }, []);

  const onConnect = useCallback(
    (connection: any) => {
      setEdges((eds) => {
        const newEdge = {
          id: `edge-${connection.source}-${connection.target}-${Date.now()}`,
          source: connection.source,
          target: connection.target,
          sourceHandle: connection.sourceHandle,
          targetHandle: connection.targetHandle,
          animated: false,
          type: "deleteButtonEdge",
          data: { onEdgeDelete: onEdgesDelete },
        };
        return [...eds, newEdge];
      });
    },
    [onEdgesDelete],
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node as Node<any>);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleSaveWorkflow = useCallback(
    (skipValidation: boolean = false) => {
      if (nodes.length === 0) {
        setValidationErrors("Workflow must contain at least one node.");
        showToast("⚠️ Workflow must contain at least one node");
        return;
      }

      if (!skipValidation) {
        const validation = validateWorkflow(nodes, edges);
        if (!validation.isValid) {
          const errorMessage = formatValidationErrors(validation);
          setValidationErrors(errorMessage);
          console.warn("⚠️ Validation errors:", errorMessage);
          showToast(
            "⚠️ Some nodes have validation errors. Check console for details.",
          );
          return null;
        }
      }

      const outgoingMap: Record<string, string[]> = {};
      const incomingCount: Record<string, number> = {};
      nodes.forEach((node) => {
        outgoingMap[node.id] = [];
        incomingCount[node.id] = 0;
      });
      edges.forEach((edge) => {
        if (edge.source && edge.target) {
          outgoingMap[edge.source].push(edge.target);
          incomingCount[edge.target] = (incomingCount[edge.target] || 0) + 1;
        }
      });

      // Trigger-type nodes are valid start nodes even when they have incoming
      // edges (e.g. when chained for orchestration). Fall back to the first
      // zero-incoming node if no explicit trigger is on the canvas.
      const TRIGGER_NODE_TYPES = new Set([
        "DOCUMENT-STORE",
        "SCHEDULE",
        "WEBHOOK",
        "HTTP",
        "GMAIL-TRIGGER",
        "OUTLOOK-CONNECTOR",
        "ONEDRIVE-TRIGGER",
      ]);
      const startNode =
        nodes.find((n) => TRIGGER_NODE_TYPES.has((n.type || "").toUpperCase())) ??
        nodes.find((node) => (incomingCount[node.id] || 0) === 0);
      if (!startNode) {
        console.warn("⚠️ No start node found — all nodes have incoming edges!");
        return;
      }

      const visited = new Set<string>();
      const orderedNodes: Node<any>[] = [];
      const queue: string[] = [startNode.id];
      while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        const node = nodes.find((n) => n.id === currentId);
        if (node) orderedNodes.push(node);
        (outgoingMap[currentId] || []).forEach((nextId) => {
          if (!visited.has(nextId)) queue.push(nextId);
        });
      }

      const workflowPayload = orderedNodes.map((node, index) => {
        const nodeDataForPayload = { ...node.data };

        // Special handling for SWITCH node transformation
        // Check for rules array specifically to identify switch logic regardless of strict type casing
        if (
          (node.type === "SWITCH" || node.type?.toUpperCase() === "SWITCH") &&
          Array.isArray(nodeDataForPayload.rules)
        ) {
          nodeDataForPayload.cases = nodeDataForPayload.rules.map(
            (rule: any, idx: number) => ({
              output_index: idx,
              conditions:
                rule.operator === "else"
                  ? []
                  : [
                      {
                        key: rule.value,
                        operator: rule.operator,
                        value: rule.compareValue,
                      },
                    ],
            }),
          );
          delete nodeDataForPayload.rules;
        }
        delete nodeDataForPayload.outputs;
        delete nodeDataForPayload.executionStatus;
        delete nodeDataForPayload.name; // Remove name from inputs as it's at top level

        return {
          id: node.id,
          index: index + 1,
          name: node.data.name || node.id, // Use factory name or fallback to ID
          type: getNodeTypeEnum(node.type || ""),
          position: {
            x: Math.round(node.position.x),
            y: Math.round(node.position.y),
          },
          data: {
            inputs: nodeDataForPayload,
          },
        };
      });

      const nodeOrderMap = new Map(
        orderedNodes.map((node, index) => [node.id, index]),
      );
      const sortedEdges = [...edges].sort((a, b) => {
        const orderA = nodeOrderMap.get(a.source) ?? Infinity;
        const orderB = nodeOrderMap.get(b.source) ?? Infinity;
        return orderA - orderB;
      });

      // Collect tool node IDs (targets of "tool" handle edges)
      const saveToolNodeIds = new Set(
        sortedEdges
          .filter((edge) => edge.sourceHandle === "tool")
          .map((edge) => edge.target),
      );

      const edgesPayload = [
        { source: "START", target: startNode.id },
        ...sortedEdges
          .filter((edge) => !saveToolNodeIds.has(edge.source)) // skip edges FROM tool nodes
          .map((edge) => {
            const payload: any = {
              source: edge.source,
              target: edge.target,
            };
            if (edge.sourceHandle) payload.sourceHandle = edge.sourceHandle;
            if (edge.targetHandle) payload.targetHandle = edge.targetHandle;
            return payload;
          }),
        // Connect ALL leaf nodes to END (exclude error/tool handle edges, exclude tool nodes)
        ...orderedNodes
          .filter(
            (node) =>
              !saveToolNodeIds.has(node.id) &&
              !edges.some(
                (edge) =>
                  edge.source === node.id &&
                  edge.sourceHandle !== "error" &&
                  edge.sourceHandle !== "tool",
              ),
          )
          .map((node) => {
            const t = node.type?.toUpperCase?.() || node.type || "";
            if (t === "VALIDATOR" || t === "CRITIC") {
              return { source: node.id, target: "END", sourceHandle: "pass" };
            }
            if (t === "FOR-EACH") {
              return { source: node.id, target: "END", sourceHandle: "done" };
            }
            return { source: node.id, target: "END" };
          }),
      ];

      const finalWorkflow = {
        title,
        description: version,
        nodes: workflowPayload,
        edges: edgesPayload,
      };

      // Auto-inject missing "pass" edge for VALIDATOR/CRITIC nodes
      for (const node of orderedNodes) {
        const t = (node.type || "").toUpperCase();
        if (t === "VALIDATOR" || t === "CRITIC") {
          const hasPass = edgesPayload.some(
            (e: any) => e.source === node.id && e.sourceHandle === "pass",
          );
          if (!hasPass) {
            edgesPayload.push({
              source: node.id,
              target: "END",
              sourceHandle: "pass",
            });
          }
        }
      }

      console.log(
        "🔥 Final Workflow Payload:",
        JSON.stringify(finalWorkflow, null, 2),
      );

      return finalWorkflow;
    },
    [nodes, edges, title, version, setValidationErrors, showToast],
  );

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleExportWorkflow = useCallback(() => {
    if (nodes.length === 0) {
      showToast("⚠️ Nothing to export. Add at least one node.");
      return;
    }

    try {
      const exportPayload = {
        title,
        description: version,
        nodes: nodes.map((node) => {
          const nodeData = deepClone(node.data ?? {}) as any;
          delete nodeData.outputs;
          delete nodeData.executionStatus;
          delete nodeData.onExecuteNode;
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: nodeData,
          };
        }),
        edges: edges.map((edge) => {
          const edgeData = deepClone(edge.data ?? {}) as any;
          delete edgeData?.onEdgeDelete;

          return {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            animated: edge.animated ?? false,
            type: edge.type || "deleteButtonEdge",
            ...(edgeData && Object.keys(edgeData).length > 0
              ? { data: edgeData }
              : {}),
          };
        }),
      };

      const safeTitle = (title || "workflow")
        .trim()
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, "-")
        .replace(/\s+/g, "_");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const fileName = `${safeTitle || "workflow"}-${timestamp}.json`;

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showToast("✅ Workflow exported successfully!");
    } catch (error) {
      console.error("❌ Failed to export workflow:", error);
      showToast("❌ Failed to export workflow.");
    }
  }, [nodes, edges, title, version, showToast]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonContent = e.target?.result as string;
          const workflowData = JSON.parse(jsonContent);

          if (!workflowData.nodes || !workflowData.edges) {
            alert(
              "Invalid workflow JSON format. Must contain 'nodes' and 'edges' arrays.",
            );
            return;
          }

          const processedNodes = processImportedNodes(workflowData.nodes);
          setNodes(processedNodes);
          const processedEdges = processInitialEdges(workflowData.edges);
          setEdges(
            processedEdges.map((edge) => ({
              ...edge,
              data: { ...edge.data, onEdgeDelete: onEdgesDelete },
            })),
          );
          if (workflowData.title) setTitle(workflowData.title);
          if (workflowData.description) setVersion(workflowData.description);

          setWorkflowDbId(null);

          console.log("✅ Workflow imported successfully!", workflowData);
          showToast("✅ Workflow imported successfully!");
        } catch (error) {
          console.error("❌ Failed to import workflow:", error);
          alert("Failed to parse workflow JSON. Please check the file format.");
        }
      };

      reader.readAsText(file);
      event.target.value = "";
    },
    [onEdgesDelete],
  );

  const addNode = useCallback(
    (type: string) => {
      const newNode = createNodeByType(
        type,
        {
          x: Math.random() * 400 + 300,
          y: Math.random() * 300 + 100,
        },
        nodes,
      );
      setNodes((nds) => [...nds, newNode]);
      setSidebarOpen(false);
    },
    [nodes],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow");
      if (typeof type === "undefined" || !type) {
        return;
      }
      const reactFlowBounds = event.currentTarget.getBoundingClientRect();
      const position = {
        x: event.clientX - reactFlowBounds.left - 64,
        y: event.clientY - reactFlowBounds.top - 20,
      };
      const newNode = createNodeByType(type, position, nodes);
      setNodes((nds) => nds.concat(newNode));
    },
    [nodes],
  );

  const existingNodeTypes = nodes
    .map((node) => node.type)
    .filter((t): t is string => !!t);

  const handleBackClick = useCallback(() => {
    if (nodes.length > 0 && isDirty) {
      setShowExitConfirmModal(true);
    } else {
      navigate("/workflow");
    }
  }, [nodes.length, isDirty, navigate]);

  const handleSaveAndExit = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);

    if (nodes.length === 0) {
      showToast("⚠️ Please add at least one node before saving.");
      setIsSaving(false);
      return;
    }

    const outgoingMap: Record<string, string[]> = {};
    const incomingCount: Record<string, number> = {};
    nodes.forEach((node) => {
      outgoingMap[node.id] = [];
      incomingCount[node.id] = 0;
    });
    edges.forEach((edge) => {
      if (edge.source && edge.target) {
        outgoingMap[edge.source].push(edge.target);
        incomingCount[edge.target] = (incomingCount[edge.target] || 0) + 1;
      }
    });

    const TRIGGER_NODE_TYPES = new Set([
      "DOCUMENT-STORE",
      "SCHEDULE",
      "WEBHOOK",
      "HTTP",
      "GMAIL-TRIGGER",
      "OUTLOOK-CONNECTOR",
      "ONEDRIVE-TRIGGER",
    ]);
    const startNode =
      nodes.find((n) => TRIGGER_NODE_TYPES.has((n.type || "").toUpperCase())) ??
      nodes.find((node) => (incomingCount[node.id] || 0) === 0);
    if (!startNode) {
      showToast("⚠️ Missing start node.");
      setIsSaving(false);
      return;
    }

    const visited = new Set<string>();
    const orderedNodes: Node<any>[] = [];
    const queue: string[] = [startNode.id];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const node = nodes.find((n) => n.id === currentId);
      if (node) orderedNodes.push(node);
      (outgoingMap[currentId] || []).forEach((nextId) => {
        if (!visited.has(nextId)) queue.push(nextId);
      });
    }

    const templatePayload = orderedNodes.map((node, index) => {
      const nodeDataForPayload = { ...node.data };
      delete nodeDataForPayload.outputs;
      delete nodeDataForPayload.executionStatus;
      delete nodeDataForPayload.name; // Remove name from inputs as it's at top level

      return {
        id: node.id,
        index: index + 1,
        name: node.data.name || node.id, // Use factory name or fallback to ID
        type: getNodeTypeEnum(node.type || ""),
        position: {
          x: Math.round(node.position.x),
          y: Math.round(node.position.y),
        },
        data: {
          inputs: nodeDataForPayload,
        },
      };
    });

    const nodeOrderMap = new Map(
      orderedNodes.map((node, index) => [node.id, index]),
    );
    const sortedEdges = [...edges].sort((a, b) => {
      const orderA = nodeOrderMap.get(a.source) ?? Infinity;
      const orderB = nodeOrderMap.get(b.source) ?? Infinity;
      return orderA - orderB;
    });

    // Find terminal nodes (nodes with no outgoing edges, excluding error handle edges)
    const nodesWithOutgoing = new Set(
      sortedEdges
        .filter((edge) => edge.sourceHandle !== "error")
        .map((edge) => edge.source),
    );
    const terminalNodes = orderedNodes.filter(
      (node) => !nodesWithOutgoing.has(node.id),
    );

    // If no terminal nodes found, fall back to last ordered node
    const endNodes =
      terminalNodes.length > 0
        ? terminalNodes
        : [orderedNodes[orderedNodes.length - 1]];

    const edgesPayload = [
      { source: "START", target: startNode.id },
      ...sortedEdges.map((edge) => {
        const payload: any = {
          source: edge.source,
          target: edge.target,
        };
        if (edge.sourceHandle) payload.sourceHandle = edge.sourceHandle;
        if (edge.targetHandle) payload.targetHandle = edge.targetHandle;
        return payload;
      }),
      ...endNodes
        .filter((node) => node) // safety check
        .map((node) => {
          const t = node.type?.toUpperCase?.() || node.type || "";
          if (t === "VALIDATOR" || t === "CRITIC") {
            return { source: node.id, target: "END", sourceHandle: "pass" };
          }
          if (t === "FOR-EACH") {
            return { source: node.id, target: "END", sourceHandle: "done" };
          }
          return { source: node.id, target: "END" };
        }),
    ];

    const finalTemplate = {
      title,
      description: version,
      is_active: isActive,
      nodes: templatePayload,
      edges: edgesPayload,
    };

    try {
      if (workflowDbId) {
        localStorage.setItem("template_id", workflowDbId);
        await apiClient.put(
          `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflowDbId}`,
          finalTemplate,
        );
      } else {
        await apiClient.post(
          API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD,
          finalTemplate,
        );
      }

      setShowExitConfirmModal(false);
      navigate("/workflow");
    } catch (error: any) {
      console.error("❌ Failed to save workflow:", error);
      showToast(`Error: ${error.response?.data?.message || "Failed to save"}`);
    } finally {
      setIsSaving(false);
    }
  }, [
    nodes,
    edges,
    title,
    version,
    isActive,
    isSaving,
    workflowDbId,
    navigate,
  ]);

  const handleDiscardAndExit = useCallback(() => {
    setShowExitConfirmModal(false);
    navigate("/workflow");
  }, [navigate]);

  // Poll execution status by ID
  const pollExecutionStatus = useCallback(
    async (workflowId: string) => {
      try {
        const response = await apiClient.get(
          API_ENDPOINTS.WORKFLOW.EXECUTION_BY_TEMPLATE_ID(workflowId),
        );

        if (response.data.success && response.data.data) {
          const executionData = response.data.data;
          const overallStatus = executionData.overall_proccessing_status;

          console.log("📊 Execution status:", overallStatus, executionData);

          // Update nodes with outputs from execution
          if (executionData.nodes && Array.isArray(executionData.nodes)) {
            setNodes((prevNodes) => {
              // Derive the running frontier from completed (DONE/FAILED) statuses.
              const statusMap = computeExecStatusMap(
                prevNodes,
                edges,
                executionData.node_status,
                overallStatus,
              );

              return prevNodes.map((node) => {
                const executedNode = executionData.nodes.find(
                  (n: any) => n.id === node.id,
                );

                const executionStatus = statusMap.get(node.id) ?? null;

                if (executionStatus === "success") {
                  // Check if this node just completed (wasn't updated before)
                  if (
                    !updatedNodeIds.has(node.id) &&
                    executedNode?.data?.outputs
                  ) {
                    console.log(
                      `✅ Node ${node.id} completed! Outputs:`,
                      executedNode.data.outputs,
                    );
                    setUpdatedNodeIds((prev) => new Set([...prev, node.id]));
                  }
                }

                if (executedNode && executedNode.data) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      ...executedNode.data.inputs,
                      outputs: executedNode.data.outputs || node.data.outputs,
                      executionStatus,
                      name: node.type === "HTTP" ? "HttpNode" : node.data.name,
                    },
                  };
                }

                // No execution payload yet, but the frontier may still mark it running.
                return { ...node, data: { ...node.data, executionStatus } };
              });
            });
          }

          // Check if execution is complete
          if (overallStatus === "DONE" || overallStatus == "WARNING") {
            showToast("✅ Workflow executed successfully!");
            setIsRunning(false);
            setPollingAttempts(0);
            setUpdatedNodeIds(new Set()); // Reset for next execution

            // Clear execution status after 3 seconds
            setTimeout(() => {
              setNodes((prevNodes) =>
                prevNodes.map((n) => ({
                  ...n,
                  data: {
                    ...n.data,
                    executionStatus: null,
                  },
                })),
              );
            }, 3000);

            return true; // Stop polling
          } else if (overallStatus === "FAILED" || overallStatus === "ERROR") {
            showToast("❌ Workflow execution failed!");
            setIsRunning(false);
            setPollingAttempts(0);
            setUpdatedNodeIds(new Set()); // Reset
            return true; // Stop polling
          }

          return false; // Continue polling
        }

        return false;
      } catch (error: any) {
        // Silently fail for network/CORS errors
        const isNetworkError =
          error.code === "ERR_NETWORK" || error.message === "Network Error";
        if (!isNetworkError) {
          console.error("❌ Failed to poll execution status:", error);
        }
        return false;
      }
    },
    [setNodes, showToast, updatedNodeIds, edges],
  );

  // Start polling execution status
  useEffect(() => {
    if (!executionId || !isRunning || !workflowId) return;

    let attempts = 0;
    let isActive = true;

    const pollInterval = setInterval(async () => {
      if (!isActive) return;

      if (attempts >= MAX_POLLING_ATTEMPTS) {
        console.warn("⚠️ Max polling attempts reached");
        showToast("⚠️ Execution is taking longer than expected");
        setIsRunning(false);
        setPollingAttempts(0);
        clearInterval(pollInterval);
        return;
      }

      const shouldStop = await pollExecutionStatus(workflowId);
      if (shouldStop) {
        clearInterval(pollInterval);
      } else {
        attempts++;
        setPollingAttempts(attempts);
      }
    }, POLLING_INTERVAL);

    return () => {
      isActive = false;
      clearInterval(pollInterval);
    };
  }, [executionId, isRunning, pollExecutionStatus, showToast, workflowId]);

  // Background polling for execution updates
  const backgroundPollExecution = useCallback(async () => {
    if (!workflowDbId) return;

    try {
      const response = await apiClient.get(
        API_ENDPOINTS.WORKFLOW.EXECUTION_BY_TEMPLATE_ID(workflowDbId),
      );

      if (response.data.success && response.data.data) {
        const executionData = response.data.data;
        const overallStatus = executionData.overall_proccessing_status;

        console.log("🔄 Background polling - Overall status:", overallStatus);

        // Update nodes with outputs AND status from polling response ONLY
        if (executionData.nodes && Array.isArray(executionData.nodes)) {
          let nodesWithCompleteOutputs = 0;
          let totalNodesCount = 0;
          let hasAnyUpdates = false;

          setNodes((prevNodes) => {
            totalNodesCount = prevNodes.length;

            // Running frontier derived from completed (DONE/FAILED) statuses —
            // the backend has no per-node "running", so we compute it here.
            const statusMap = computeExecStatusMap(
              prevNodes,
              edges,
              executionData.node_status,
              overallStatus,
            );

            return prevNodes.map((node) => {
              const executedNode = executionData.nodes.find(
                (n: any) => n.id === node.id,
              );

              if (executedNode) {
                const executionStatus = statusMap.get(node.id) ?? null;

                // Check if this node has outputs
                const hasOutputs =
                  executedNode.data?.outputs &&
                  Object.keys(executedNode.data.outputs).length > 0;

                if (hasOutputs) {
                  nodesWithCompleteOutputs++;
                }

                // Check if anything changed
                const currentOutputs = JSON.stringify(node.data.outputs || {});
                const newOutputs = JSON.stringify(
                  executedNode.data?.outputs || {},
                );
                const currentStatus = node.data.executionStatus;

                const outputsChanged = currentOutputs !== newOutputs;
                const statusChanged = currentStatus !== executionStatus;

                if (outputsChanged || statusChanged) {
                  if (outputsChanged) {
                    console.log(
                      `✅ Polling - Updated outputs for ${
                        node.data.name || node.id
                      }`,
                    );
                  }
                  if (statusChanged) {
                    console.log(
                      `📊 Polling - Status changed for ${
                        node.data.name || node.id
                      }: ${currentStatus} → ${executionStatus}`,
                    );
                  }
                  hasAnyUpdates = true;

                  return {
                    ...node,
                    data: {
                      ...node.data,
                      // Update outputs if available
                      ...(executedNode.data?.outputs && {
                        outputs: executedNode.data.outputs,
                      }),
                      // Update inputs if available
                      ...(executedNode.data?.inputs || {}),
                      // Always set status from polling
                      executionStatus,
                      // Force HttpNode name for HTTP type nodes
                      name: node.type === "HTTP" ? "HttpNode" : node.data.name,
                    },
                  };
                }

                // Even if no outputs changed, still update status if it changed
                if (statusChanged) {
                  console.log(
                    `📊 Polling - Status update for ${
                      node.data.name || node.id
                    }: ${executionStatus}`,
                  );
                  hasAnyUpdates = true;

                  return {
                    ...node,
                    data: {
                      ...node.data,
                      executionStatus,
                      name: node.type === "HTTP" ? "HttpNode" : node.data.name,
                    },
                  };
                }
              }

              return node;
            });
          });

          // Log polling progress
          console.log(
            `📊 Polling status: ${nodesWithCompleteOutputs}/${totalNodesCount} nodes have outputs | Overall: ${overallStatus}`,
          );

          // Never stop polling - continue regardless of status (IN_PROGRESS, DONE, FAILED, etc.)
          if (hasAnyUpdates) {
            console.log(
              `🔄 Background poll - Updates received (Status: ${overallStatus}), continuing to poll...`,
            );
          } else {
            console.log(
              `🔄 Background poll - Status: ${overallStatus}, continuing to poll...`,
            );
          }
        }
      }
    } catch (error: any) {
      // Silently fail for 404s (no execution yet) and network/CORS errors
      const isNetworkError =
        error.code === "ERR_NETWORK" || error.message === "Network Error";
      if (error.response?.status !== 404 && !isNetworkError) {
        console.error("❌ Background polling error:", error.message);
      }
    }
  }, [workflowDbId, setNodes, hasIncompleteExecutionData, edges]);

  // Start background polling when workflowDbId is available
  // Polling never stops automatically - it continues indefinitely for all workflow states
  useEffect(() => {
    // Only stop polling if no workflow ID or during initial loading
    if (!workflowDbId || isLoadingWorkflow || !hasIncompleteExecutionData) {
      if (backgroundPollingIntervalRef.current) {
        console.log("🛑 Pausing background polling - Waiting for workflow");
        clearInterval(backgroundPollingIntervalRef.current);
        backgroundPollingIntervalRef.current = null;
      }
      return;
    }

    // Start continuous polling - will never stop unless component unmounts
    if (hasIncompleteExecutionData) {
      console.log(
        "🎧 Starting continuous background polling for template:",
        workflowDbId,
      );

      // Poll immediately
      backgroundPollExecution();

      // Set up background polling interval - runs indefinitely
      backgroundPollingIntervalRef.current = setInterval(() => {
        backgroundPollExecution();
      }, BACKGROUND_POLLING_INTERVAL);
    }

    // Cleanup only on unmount or when workflow changes
    return () => {
      if (backgroundPollingIntervalRef.current) {
        console.log("🛑 Stopping background polling - Component cleanup");
        clearInterval(backgroundPollingIntervalRef.current);
        backgroundPollingIntervalRef.current = null;
      }
    };
  }, [
    workflowDbId,
    isLoadingWorkflow,
    hasIncompleteExecutionData,
    backgroundPollExecution,
  ]);

  // Animate the edges flowing INTO whichever node is currently running, so the
  // canvas shows data moving like n8n. Only writes to edge state when the active
  // set actually changes — no render loop, and idle (no running nodes) is a no-op.
  useEffect(() => {
    const runningIds = new Set(
      nodes
        .filter((n) => n.data?.executionStatus === "running")
        .map((n) => n.id),
    );
    setEdges((prev) => {
      let changed = false;
      const next = prev.map((e) => {
        const active = runningIds.has(e.target);
        if (!!(e.data as any)?.isActive !== active || !!e.animated !== active) {
          changed = true;
          return {
            ...e,
            animated: active,
            data: { ...e.data, isActive: active },
          };
        }
        return e;
      });
      return changed ? next : prev;
    });
  }, [nodes]);

  // Auto-save when nodes or edges change (debounced)
  useEffect(() => {
    // Only auto-save if template already exists
    if (!workflowDbId || isSaving || isRunning) return;

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    // Set new timeout for auto-save (2 seconds after last change)
    const timeout = setTimeout(async () => {
      if (nodes.length > 0) {
        console.log("🔄 Auto-saving template...");
        const payload = handleSaveWorkflow(false);
        if (payload) {
          try {
            await apiClient.put(
              `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflowDbId}`,
              payload,
            );
            setHasUnsavedChanges(false);
            console.log("✅ Template auto-saved");
          } catch (error) {
            console.error("❌ Auto-save failed:", error);
          }
        }
      }
    }, 2000);

    setAutoSaveTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [nodes, edges]);

  const handleRunWorkflow = useCallback(async () => {
    // Validate workflow before running
    const validation = validateWorkflow(nodes, edges);
    if (!validation.isValid) {
      const errorMessage = formatValidationErrors(validation);
      console.warn("⚠️ Validation errors:", errorMessage);
      showToast("⚠️ Please fix validation errors before running");
      return;
    }

    if (!workflowDbId) {
      showToast("⚠️ Please save the template first before running");
      return;
    }

    // If there are unsaved changes, save first
    if (hasUnsavedChanges) {
      console.log("🔄 Saving changes before execution...");
      const payload = handleSaveWorkflow(false);
      if (payload) {
        try {
          localStorage.setItem("template_id", workflowDbId);
          await apiClient.put(
            `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflowDbId}`,
            payload,
          );
          setHasUnsavedChanges(false);
          console.log("✅ Changes saved");
        } catch (error) {
          console.error("❌ Save failed:", error);
          showToast("❌ Failed to save changes before execution");
          return;
        }
      }
    }

    setIsRunning(true);
    setExecutionResults([]);
    setPollingAttempts(0);
    setUpdatedNodeIds(new Set()); // Reset tracking for new execution

    // Clear any previous execution status - let polling handle the actual status
    setNodes((prevNodes) =>
      prevNodes.map((n) => ({
        ...n,
        data: {
          ...n.data,
          executionStatus: null,
        },
      })),
    );

    try {
      console.log("🚀 Starting workflow execution...");

      // Prepare execution payload - must include full node data with inputs
      const outgoingMap: Record<string, string[]> = {};
      const incomingCount: Record<string, number> = {};
      nodes.forEach((node) => {
        outgoingMap[node.id] = [];
        incomingCount[node.id] = 0;
      });
      edges.forEach((edge) => {
        if (edge.source && edge.target) {
          outgoingMap[edge.source].push(edge.target);
          incomingCount[edge.target] = (incomingCount[edge.target] || 0) + 1;
        }
      });

      const TRIGGER_NODE_TYPES = new Set([
        "DOCUMENT-STORE",
        "SCHEDULE",
        "WEBHOOK",
        "HTTP",
        "GMAIL-TRIGGER",
        "OUTLOOK-CONNECTOR",
        "ONEDRIVE-TRIGGER",
      ]);
      const startNode =
        nodes.find((n) => TRIGGER_NODE_TYPES.has((n.type || "").toUpperCase())) ??
        nodes.find((node) => (incomingCount[node.id] || 0) === 0);
      if (!startNode) {
        showToast("⚠️ No start node found");
        setIsRunning(false);
        return;
      }

      const visited = new Set<string>();
      const orderedNodes: Node<any>[] = [];
      const queue: string[] = [startNode.id];
      while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        const node = nodes.find((n) => n.id === currentId);
        if (node) orderedNodes.push(node);
        (outgoingMap[currentId] || []).forEach((nextId) => {
          if (!visited.has(nextId)) queue.push(nextId);
        });
      }

      const executionPayload = orderedNodes.map((node, index) => {
        const nodeDataForPayload = { ...node.data };
        delete nodeDataForPayload.outputs;
        delete nodeDataForPayload.executionStatus;
        delete nodeDataForPayload.name; // Remove name from inputs as it's at top level

        return {
          id: node.id,
          index: index + 1,
          name: node.data.name || node.id, // Use factory name or fallback to ID
          type: getNodeTypeEnum(node.type || ""),
          position: {
            x: Math.round(node.position.x),
            y: Math.round(node.position.y),
          },
          data: {
            inputs: nodeDataForPayload,
          },
        };
      });

      const nodeOrderMap = new Map(
        orderedNodes.map((node, index) => [node.id, index]),
      );
      const sortedEdges = [...edges].sort((a, b) => {
        const orderA = nodeOrderMap.get(a.source) ?? Infinity;
        const orderB = nodeOrderMap.get(b.source) ?? Infinity;
        return orderA - orderB;
      });

      // Collect tool node IDs (targets of "tool" handle edges — they execute inline, not as graph nodes)
      const execToolNodeIds = new Set(
        sortedEdges
          .filter((edge) => edge.sourceHandle === "tool")
          .map((edge) => edge.target),
      );

      // Find terminal nodes (exclude error/tool handle edges, exclude tool nodes)
      const execNodesWithOutgoing = new Set(
        sortedEdges
          .filter(
            (edge) =>
              edge.sourceHandle !== "error" && edge.sourceHandle !== "tool",
          )
          .map((edge) => edge.source),
      );
      const execTerminalNodes = orderedNodes.filter(
        (node) =>
          !execNodesWithOutgoing.has(node.id) && !execToolNodeIds.has(node.id),
      );
      const execEndNodes =
        execTerminalNodes.length > 0
          ? execTerminalNodes
          : [orderedNodes[orderedNodes.length - 1]];

      const edgesPayload = [
        { source: "START", target: startNode.id },
        ...sortedEdges
          .filter((edge) => !execToolNodeIds.has(edge.source)) // skip edges FROM tool nodes
          .map((edge) => {
            const payload: any = {
              source: edge.source,
              target: edge.target,
            };
            if (edge.sourceHandle) payload.sourceHandle = edge.sourceHandle;
            if (edge.targetHandle) payload.targetHandle = edge.targetHandle;
            return payload;
          }),
        ...execEndNodes
          .filter((node) => node)
          .map((node) => {
            const t = node.type?.toUpperCase?.() || node.type || "";
            // Validator/Critic nodes need "pass" handle, FOR-EACH needs "done" handle
            if (t === "VALIDATOR" || t === "CRITIC") {
              return { source: node.id, target: "END", sourceHandle: "pass" };
            }
            if (t === "FOR-EACH") {
              return { source: node.id, target: "END", sourceHandle: "done" };
            }
            return { source: node.id, target: "END" };
          }),
      ];

      // Auto-inject missing "pass" edge for VALIDATOR/CRITIC nodes that have retry but no pass
      for (const node of orderedNodes) {
        const t = (node.type || "").toUpperCase();
        if (t === "VALIDATOR" || t === "CRITIC") {
          const hasPass = edgesPayload.some(
            (e: any) => e.source === node.id && e.sourceHandle === "pass",
          );
          if (!hasPass) {
            edgesPayload.push({
              source: node.id,
              target: "END",
              sourceHandle: "pass",
            });
          }
        }
      }

      const finalExecutionPayload = {
        template_id: workflowDbId,
        nodes: executionPayload,
        edges: edgesPayload,
      };

      console.log(
        "📤 Execution payload:",
        JSON.stringify(finalExecutionPayload, null, 2),
      );

      // Call execution API
      const executionResponse = await apiClient.post(
        API_ENDPOINTS.WORKFLOW.EXECUTION,
        finalExecutionPayload,
      );

      if (executionResponse.data.success && executionResponse.data.data) {
        const execId =
          executionResponse.data.data._id || executionResponse.data.data.id;
        console.log("✅ Execution started with ID:", execId);
        setExecutionId(execId);

        // Start polling immediately
        const shouldStop = await pollExecutionStatus(workflowDbId);
        if (!shouldStop) {
          // Polling will continue via useEffect
          console.log("🔄 Polling started...");
        }
      } else {
        throw new Error("Failed to start execution");
      }
    } catch (error: any) {
      console.error("❌ Failed to execute workflow:", error);

      // Skip showing toast for network/CORS errors - these are often transient
      const isNetworkError =
        error.code === "ERR_NETWORK" || error.message === "Network Error";

      if (isNetworkError) {
        console.warn(
          "⚠️ Network error occurred (possibly CORS), suppressing toast",
        );
        setIsRunning(false);
        return;
      }

      // Mark all nodes as error
      setNodes((prevNodes) =>
        prevNodes.map((n) => ({
          ...n,
          data: {
            ...n.data,
            executionStatus: "error",
          },
        })),
      );

      // Extract error message from different possible structures
      let errorMessage = "Failed to execute workflow. Please try again.";

      if (error.response?.data?.error?.error_description) {
        // Handle structure: { success: false, error: { error_code, error_description } }
        const errorData = error.response.data.error;
        errorMessage = `${errorData.error_code || "Error"}: ${
          errorData.error_description
        }`;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        // If error is a string
        errorMessage =
          typeof error.response.data.error === "string"
            ? error.response.data.error
            : JSON.stringify(error.response.data.error);
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(`❌ ${errorMessage}`);
      setIsRunning(false);
    }
  }, [
    workflowDbId,
    nodes,
    edges,
    handleSaveWorkflow,
    hasUnsavedChanges,
    showToast,
    pollExecutionStatus,
  ]);

  const handleSaveTemplate = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);

    if (nodes.length === 0) {
      showToast("⚠️ Please add at least one node before saving.");
      setIsSaving(false);
      return;
    }

    const outgoingMap: Record<string, string[]> = {};
    const incomingCount: Record<string, number> = {};
    nodes.forEach((node) => {
      outgoingMap[node.id] = [];
      incomingCount[node.id] = 0;
    });
    edges.forEach((edge) => {
      if (edge.source && edge.target) {
        outgoingMap[edge.source].push(edge.target);
        incomingCount[edge.target] = (incomingCount[edge.target] || 0) + 1;
      }
    });

    const TRIGGER_NODE_TYPES = new Set([
      "DOCUMENT-STORE",
      "SCHEDULE",
      "WEBHOOK",
      "HTTP",
      "GMAIL-TRIGGER",
      "OUTLOOK-CONNECTOR",
      "ONEDRIVE-TRIGGER",
    ]);
    const startNode =
      nodes.find((n) => TRIGGER_NODE_TYPES.has((n.type || "").toUpperCase())) ??
      nodes.find((node) => (incomingCount[node.id] || 0) === 0);
    if (!startNode) {
      showToast(
        "⚠️ Missing start node. Make sure at least one node has no incoming edge.",
      );
      setIsSaving(false);
      return;
    }

    const visited = new Set<string>();
    const orderedNodes: Node<any>[] = [];
    const queue: string[] = [startNode.id];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const node = nodes.find((n) => n.id === currentId);
      if (node) orderedNodes.push(node);
      (outgoingMap[currentId] || []).forEach((nextId) => {
        if (!visited.has(nextId)) queue.push(nextId);
      });
    }

    const templatePayload = orderedNodes.map((node, index) => {
      const nodeDataForPayload = { ...node.data };
      delete nodeDataForPayload.outputs;
      delete nodeDataForPayload.executionStatus;
      delete nodeDataForPayload.name; // Remove name from inputs as it's at top level

      return {
        id: node.id,
        index: index + 1,
        name: node.data.name || node.id, // Use factory name or fallback to ID
        type: getNodeTypeEnum(node.type || ""),
        position: {
          x: Math.round(node.position.x),
          y: Math.round(node.position.y),
        },
        data: {
          inputs: nodeDataForPayload,
        },
      };
    });

    const nodeOrderMap = new Map(
      orderedNodes.map((node, index) => [node.id, index]),
    );
    const sortedEdges = [...edges].sort((a, b) => {
      const orderA = nodeOrderMap.get(a.source) ?? Infinity;
      const orderB = nodeOrderMap.get(b.source) ?? Infinity;
      return orderA - orderB;
    });

    // Collect tool node IDs (targets of "tool" handle edges)
    const autoSaveToolNodeIds = new Set(
      sortedEdges
        .filter((edge) => edge.sourceHandle === "tool")
        .map((edge) => edge.target),
    );

    // Find terminal nodes (exclude error/tool handle edges, exclude tool nodes)
    const saveNodesWithOutgoing = new Set(
      sortedEdges
        .filter(
          (edge) =>
            edge.sourceHandle !== "error" && edge.sourceHandle !== "tool",
        )
        .map((edge) => edge.source),
    );
    const saveTerminalNodes = orderedNodes.filter(
      (node) =>
        !saveNodesWithOutgoing.has(node.id) &&
        !autoSaveToolNodeIds.has(node.id),
    );
    const saveEndNodes =
      saveTerminalNodes.length > 0
        ? saveTerminalNodes
        : [orderedNodes[orderedNodes.length - 1]];

    const edgesPayload = [
      { source: "START", target: startNode.id },
      ...sortedEdges
        .filter((edge) => !autoSaveToolNodeIds.has(edge.source)) // skip edges FROM tool nodes
        .map((edge) => ({
          source: edge.source,
          target: edge.target,
          ...(edge.sourceHandle ? { sourceHandle: edge.sourceHandle } : {}),
          ...(edge.targetHandle ? { targetHandle: edge.targetHandle } : {}),
        })),
      ...saveEndNodes
        .filter((node) => node)
        .map((node) => {
          const t = node.type?.toUpperCase?.() || node.type || "";
          if (t === "VALIDATOR" || t === "CRITIC") {
            return { source: node.id, target: "END", sourceHandle: "pass" };
          }
          if (t === "FOR-EACH") {
            return { source: node.id, target: "END", sourceHandle: "done" };
          }
          return { source: node.id, target: "END" };
        }),
    ];

    // Auto-inject missing "pass" edge for VALIDATOR/CRITIC nodes
    for (const node of orderedNodes) {
      const t = (node.type || "").toUpperCase();
      if (t === "VALIDATOR" || t === "CRITIC") {
        const hasPass = edgesPayload.some(
          (e: any) => e.source === node.id && e.sourceHandle === "pass",
        );
        if (!hasPass) {
          edgesPayload.push({
            source: node.id,
            target: "END",
            sourceHandle: "pass",
          });
        }
      }
    }

    const finalTemplate = {
      title,
      description: version,
      is_active: isActive,
      nodes: templatePayload,
      edges: edgesPayload,
    };

    try {
      console.log("🚀 Saving workflow with payload:", finalTemplate);

      let response;
      if (workflowDbId) {
        localStorage.setItem("template_id", workflowDbId);
        response = await apiClient.put(
          `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflowDbId}`,
          finalTemplate,
        );
        showToast("🎉 Template updated successfully!");
      } else {
        response = await apiClient.post(
          API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD,
          finalTemplate,
        );
        showToast("🎉 Template created successfully!");

        if (response.data.success && response.data.data) {
          const savedId = response.data.data._id || response.data.data.id;
          setWorkflowDbId(savedId);
          console.log("💾 Workflow ID stored:", savedId);
          localStorage.setItem("template_id", savedId);
          setNodes((prev) =>
            prev.map((node) => {
              if (node.type === "HTTP") {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    request_id: savedId, // ⭐ inject backend _id
                  },
                };
              }
              if (
                node.type === "ONEDRIVE-CONNECTOR" ||
                node.type === "ONEDRIVE-UPLOAD"
              ) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    template_id: savedId, // ⭐ inject template_id for OneDrive auth
                  },
                };
              }
              return node;
            }),
          );
        }
      }

      setIsDirty(false);
    } catch (error: any) {
      console.error("❌ Failed to save workflow:", error);

      // Handle known structured API errors
      const apiError = error.response?.data?.error;
      const statusCode = error.response?.status;

      if (statusCode === 409 && apiError?.error_code === "duplicate_title") {
        showToast(
          `⚠️ A workflow titled "${title}" already exists. Please use a different title.`,
        );
      } else {
        const errorMessage =
          apiError?.error_description ||
          error.response?.data?.message ||
          error.message ||
          "Failed to save workflow. Please try again.";
        showToast(`Error: ${errorMessage}`);
      }
    } finally {
      setIsSaving(false);
    }
  }, [nodes, edges, title, version, isActive, isSaving, workflowDbId]);

  // Names of the node(s) currently executing — drives the "Running: X" label
  // so the user sees exactly which node is active, n8n-style.
  const runningNodeNames = nodes
    .filter((n) => n.data?.executionStatus === "running")
    .map((n) => n.data?.name || n.id);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {toastMessage && (
        <div className="fixed bottom-[190px] right-4 z-[9999] transition-all duration-500 ease-out animate-[fadeInUp_0.3s_ease-out]">
          <div className="flex items-start gap-3 bg-white border-l-4 border-blue-600 text-gray-800 text-sm px-4 py-3 rounded-md shadow-md min-w-[260px] max-w-lg">
            {/* Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M12 20.5a8.38 8.38 0 100-16.76 8.38 8.38 0 000 16.76z"
              />
            </svg>

            {/* Message */}
            <div className="flex-1 min-w-0 max-h-48 overflow-y-auto pr-1">
              <span className="break-words whitespace-pre-wrap block leading-relaxed">
                {toastMessage}
              </span>
            </div>

            {/* Close button */}
            <button
              onClick={() => setToastMessage(null)}
              className="text-gray-400 hover:text-gray-600 transition flex-shrink-0 mt-0.5"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <nav className="fixed top-0 left-0 right-0 z-40 h-[66px] bg-[#fafafa] border-b border-gray-200 flex items-center justify-between pe-3 ps-4">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackClick}
            className="flex items-center justify-center w-9 h-9 rounded-md border border-gray-200 bg-white hover:bg-gray-100 transition-all"
            title="Go Back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex items-center justify-center w-9 h-9 bg-blue-100 border border-gray-200 rounded-md">
            <Workflow className="w-5 h-5 text-gray-800" />
          </div>
          <div className="flex flex-col leading-tight">
            <div className="flex items-center gap-2">
              {isEditingTitle ? (
                <>
                  <span
                    ref={titleSpanRef}
                    className="absolute invisible whitespace-pre text-[15px] font-semibold"
                  >
                    {title || " "}
                  </span>
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={handleTitleBlur}
                    onKeyDown={(e) => e.key === "Enter" && handleTitleBlur()}
                    className="text-[15px] font-semibold text-gray-800 border border-gray-300 rounded px-1 focus:outline-none focus:ring focus:ring-blue-300"
                    autoFocus
                  />
                </>
              ) : (
                <h1 className="text-[15px] font-semibold text-gray-800">
                  {title}
                </h1>
              )}
              <Pencil
                size={14}
                className="text-gray-500 cursor-pointer hover:text-gray-700"
                onClick={() => setIsEditingTitle(true)}
              />
            </div>
            <div className="flex items-center gap-2">
              {isEditingVersion ? (
                <>
                  <span
                    ref={versionSpanRef}
                    className="absolute invisible whitespace-pre text-xs"
                  >
                    {version || " "}
                  </span>
                  <input
                    ref={versionInputRef}
                    type="text"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    onBlur={handleVersionBlur}
                    onKeyDown={(e) => e.key === "Enter" && handleVersionBlur()}
                    className="text-xs text-gray-500 border border-gray-300 rounded px-1 focus:outline-none focus:ring focus:ring-blue-300"
                    autoFocus
                  />
                </>
              ) : (
                <span className="text-xs text-gray-500">{version}</span>
              )}
              <Pencil
                size={12}
                className="text-gray-400 cursor-pointer hover:text-gray-600"
                onClick={() => setIsEditingVersion(true)}
              />
            </div>
          </div>
        </div>
        {/* Center Tab Switcher */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <button
            onClick={() => setActiveTab("nodes")}
            className={`px-6 py-2 text-sm font-medium transition-all ${
              activeTab === "nodes"
                ? "bg-blue-500 text-white"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <Workflow className="w-4 h-4" />
              Nodes
            </div>
          </button>
          <button
            onClick={() => {
              setActiveTab("executions");
              setExecutionsRefreshKey((k) => k + 1);
            }}
            className={`px-6 py-2 text-sm font-medium transition-all relative ${
              activeTab === "executions"
                ? "bg-blue-500 text-white"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <PlayCircle className="w-4 h-4" />
              Executions
            </div>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            className="hidden"
          />
          <div className="flex items-center gap-2">
            <label className="flex items-center cursor-pointer">
              <span className="me-2 text-sm text-gray-700">
                {isActive ? "Active" : "Inactive"}
              </span>
              <div className="relative me-3">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`block w-10 h-5 rounded-full transition-colors duration-300 ease-in-out border ${
                    isActive
                      ? "bg-blue-600 border-blue-600 shadow-inner"
                      : "bg-slate-200 border-slate-300"
                  }`}
                ></div>
                <div
                  className={`dot absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform duration-300 ease-in-out shadow-sm ${
                    isActive ? "translate-x-5" : ""
                  }`}
                ></div>
              </div>
            </label>
          </div>

          {workflowDbId && activeTab !== "executions" && (
            <button
              onClick={() => setShowTokenAnalytics(true)}
              className="flex items-center gap-2 px-3.5 py-2 cursor-pointer text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm shadow-sm hover:bg-gray-50 transition-colors"
              title="Token Usage Analytics"
            >
              <Cpu className="w-4 h-4" />
              Token Analytics
            </button>
          )}
          <button
            onClick={handleImportClick}
            className="flex items-center gap-2 px-3.5 py-2 cursor-pointer text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm shadow-sm hover:bg-gray-50 transition-colors"
            title="Import Workflow JSON"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={handleExportWorkflow}
            className="flex items-center gap-2 px-3.5 py-2 cursor-pointer text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm shadow-sm hover:bg-gray-50 transition-colors"
            title="Export Workflow JSON"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={handleSaveTemplate}
            disabled={!isDirty || isSaving}
            className={`flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-sm shadow transition-colors
    ${
      !isDirty || isSaving
        ? "cursor-not-allowed text-gray-400 bg-gray-200"
        : "text-white bg-[#2563eb] hover:bg-[#1e4fd8]"
    }`}
          >
            {isSaving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save
              </>
            )}
          </button>
        </div>
      </nav>

      <div className="flex-1 pt-16 flex">
        {/* Nodes View */}
        {activeTab === "nodes" && (
          <div
            className="flex-1 relative"
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            {sidebarOpen && (
              <div
                className="fixed inset-0 z-40"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            {sidebarOpen && (
              <div className="fixed left-0 top-0 h-full z-50 transform transition-transform duration-300 ease-out">
                <LeftSidebar
                  onAddNode={addNode}
                  onClose={() => setSidebarOpen(false)}
                  existingNodes={existingNodeTypes}
                />
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(true)}
              className="fixed top-20 left-4 cursor-pointer z-30 p-3 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition flex items-center justify-center"
              aria-label="Add Node"
            >
              <Plus className="w-6 h-6 text-gray-600" />
            </button>
            <ReactFlowProvider>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                fitViewOptions={{ padding: 0.2, maxZoom: 0.7 }}
                className="bg-gray-50"
                panOnScroll={true}
                selectionOnDrag={true}
                nodesDraggable
                nodesConnectable
                elementsSelectable
                selectNodesOnDrag={false}
                panOnDrag={[1, 2]}
                zoomOnDoubleClick={false}
                proOptions={proOptions}
              >
                <Controls
                  orientation="horizontal"
                  className="bg-white shadow-lg rounded-lg border border-gray-200"
                />
                <MiniMap
                  className="bg-white shadow-lg rounded-lg border border-gray-200"
                  nodeColor="#e5e7eb"
                  maskColor="rgba(0,0,0,0.1)"
                />
                <Background
                  id="1"
                  gap={10}
                  color="#969595ff"
                  variant={BackgroundVariant.Dots}
                />
              </ReactFlow>
            </ReactFlowProvider>
            <button
              onClick={handleRunWorkflow}
              disabled={isRunning}
              className="fixed bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-5 py-2.5 cursor-pointer text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRunning ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span className="max-w-[260px] truncate">
                    {runningNodeNames.length > 0
                      ? `Running: ${runningNodeNames[0]}${
                          runningNodeNames.length > 1
                            ? ` +${runningNodeNames.length - 1} more`
                            : ""
                        }`
                      : "Starting workflow…"}
                  </span>
                </>
              ) : (
                <>
                  <RocketIcon className="w-4 h-4" />
                  <span>Run Workflow</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Executions View */}
        {activeTab === "executions" && (
          <div className="flex-1 bg-white overflow-hidden">
            <ExecutionsPanel
              nodes={nodes}
              isRunning={isRunning}
              executionId={executionId}
              templateId={workflowDbId || undefined}
              refreshTrigger={executionsRefreshKey}
              workflowName={title}
            />
          </div>
        )}
      </div>

      <ExitConfirmationModal
        isOpen={showExitConfirmModal}
        onClose={() => setShowExitConfirmModal(false)}
        onSaveAndExit={handleSaveAndExit}
        onDiscardAndExit={handleDiscardAndExit}
      />

      {showTokenAnalytics && workflowDbId && (
        <WorkflowTokenAnalytics
          templateId={workflowDbId}
          workflowName={title}
          onClose={() => setShowTokenAnalytics(false)}
        />
      )}
    </div>
  );
}

export default WorkFlowExecutions;
